import { memo, useCallback, useId, useMemo, useState } from 'react';
import { Check, FileText } from 'lucide-react';

/**
 * Option row to represent a selectable file/doc item with checkbox or radio indicator.
 *
 * @param {object} props
 * @param {string} [props.text='']
 * @param {import('react').ElementType} [props.icon=FileText]
 * @param {'checkbox'|'radio'} [props.selectionType='checkbox']
 * @param {boolean} [props.checked] - Controlled checked state.
 * @param {boolean} [props.defaultChecked=false] - Initial state for uncontrolled mode.
 * @param {boolean} [props.disabled=false]
 * @param {(nextChecked: boolean, event: import('react').ChangeEvent<HTMLInputElement>) => void} [props.onChange]
 * @param {(event: import('react').MouseEvent<HTMLLabelElement>) => void} [props.onClick]
 * @param {string} [props.name]
 * @param {string} [props.value]
 * @param {string} [props.id]
 * @param {string} [props.className]
 * @param {string} [props.textClassName]
 * @param {string} [props.iconClassName]
 * @param {number} [props.iconSize=11]
 * @param {string} [props.indicatorClassName]
 * @param {string} [props.code] - Optional badge rendered before the text (e.g. "BU-001").
 * @param {string} [props.codeClassName] - Extra classes for the code badge span.
 */
const SelectableFileItem = ({
  text = '',
  icon: Icon = FileText,
  selectionType = 'checkbox',
  checked,
  defaultChecked = false,
  disabled = false,
  onChange,
  onClick,
  name,
  value,
  id,
  className = '',
  textClassName = '',
  iconClassName = 'text-red-400',
  iconSize = 11,
  indicatorClassName = '',
  code,
  codeClassName = '',
}) => {
  const generatedId = useId();
  const inputId = id || `selectable-file-item-${generatedId}`;
  const isRadio = selectionType === 'radio';
  const isControlled = typeof checked === 'boolean';
  const [internalChecked, setInternalChecked] = useState(defaultChecked);
  const isChecked = isControlled ? checked : internalChecked;

  const labelClassName = useMemo(
    () =>
      [
        'flex items-center gap-2 rounded-lg border p-2.5 transition-all',
        'border-edge bg-surface dark:border-white/[0.07] dark:bg-white/[0.02]',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-edge-strong',
        className,
      ]
        .filter(Boolean)
        .join(' '),
    [className, disabled],
  );

  const indicatorBaseClass = isRadio ? 'rounded-full' : 'rounded';
  const indicatorStateClass = isChecked
    ? 'border-brand-500 bg-brand-500'
    : 'border-edge bg-transparent dark:border-white/20';

  const handleInputChange = useCallback(
    (event) => {
      if (disabled) return;

      const nextChecked = isRadio ? true : event.target.checked;
      if (!isControlled) setInternalChecked(nextChecked);
      onChange?.(nextChecked, event);
    },
    [disabled, isControlled, isRadio, onChange],
  );

  return (
    <label className={labelClassName} htmlFor={inputId} onClick={onClick}>
      <input
        id={inputId}
        type={isRadio ? 'radio' : 'checkbox'}
        name={name}
        value={value}
        checked={Boolean(isChecked)}
        disabled={disabled}
        onChange={handleInputChange}
        className="sr-only"
      />

      <span
        className={[
          'flex h-3.5 w-3.5 shrink-0 items-center justify-center border transition-colors',
          indicatorBaseClass,
          indicatorStateClass,
          indicatorClassName,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden
      >
        {isChecked ? (
          isRadio ? (
            <span className="h-1.5 w-1.5 rounded-full bg-white" />
          ) : (
            <Check size={10} strokeWidth={2.5} className="text-white" />
          )
        ) : null}
      </span>

      <Icon size={iconSize} className={['shrink-0', iconClassName].filter(Boolean).join(' ')} aria-hidden />

      {code && (
        <span
          className={[
            'shrink-0 font-mono text-[10px] text-brand-400',
            codeClassName,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {code}
        </span>
      )}

      <span
        className={['min-w-0 truncate text-xs text-content-secondary', textClassName]
          .filter(Boolean)
          .join(' ')}
        title={text}
      >
        {text}
      </span>
    </label>
  );
};

export default memo(SelectableFileItem);
